<script setup>
import { ref, computed, nextTick } from 'vue';
import { useAuthService } from '~/composables/useAuthService';
import ConversationListItem from './ConversationListItem.vue';

const props = defineProps({
  conversations: {
    type: Array,
    default: () => []
  },
  activeConversationId: {
    type: String,
    default: null
  },
  syncInProgress: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['select-conversation', 'new-conversation', 'sync', 'delete']);

const { user } = useAuthService();

// Search and filter state
const searchQuery = ref('');
const filterMode = ref('all'); // 'all' or 'unread'
const showSearch = ref(false);
const searchInput = ref(null);

// Toggle search and reset filters when hiding
const toggleSearch = async () => {
  showSearch.value = !showSearch.value;
  if (!showSearch.value) {
    searchQuery.value = '';
    filterMode.value = 'all';
  } else {
    // Focus search input when shown
    await nextTick();
    searchInput.value?.focus();
  }
};

// Get conversation title for searching
const getConversationTitle = (conversation) => {
  if (conversation.title) {
    return conversation.title;
  }

  // For one-to-one chats, get other participant's name
  if (conversation.conversationType === 1 && conversation.participants) {
    const otherParticipant = conversation.participants.find(p => p.id !== user.value?.id);
    if (otherParticipant) {
      return `${otherParticipant.firstName} ${otherParticipant.lastName}`;
    }
  }

  return 'Conversation';
};

// Get searchable participant names
const getParticipantNames = (conversation) => {
  if (!conversation.participants) return '';
  return conversation.participants
    .map(p => `${p.firstName} ${p.lastName}`.toLowerCase())
    .join(' ');
};

// Filtered conversations based on search and filter
const filteredConversations = computed(() => {
  let filtered = props.conversations;

  // Apply unread filter
  if (filterMode.value === 'unread') {
    filtered = filtered.filter(conv => conv.unreadCount > 0);
  }

  // Apply search filter
  if (searchQuery.value.trim()) {
    const query = searchQuery.value.toLowerCase().trim();
    filtered = filtered.filter(conv => {
      const title = getConversationTitle(conv).toLowerCase();
      const participantNames = getParticipantNames(conv);
      return title.includes(query) || participantNames.includes(query);
    });
  }

  return filtered;
});

const handleSelectConversation = (conversation) => {
  emit('select-conversation', conversation);
};

const handleDeleteConversation = (conversation) => {
  emit('delete', conversation);
};
</script>

<template>
  <div class="conv-list">
    <!-- Header: title + search / new / sync -->
    <div class="list-head">
      <div class="lh-title">Messages</div>
      <button
          type="button"
          class="list-iconbtn"
          :aria-label="showSearch ? 'Close search' : 'Search conversations'"
          @click="toggleSearch"
      >
        <v-icon size="19">{{ showSearch ? 'mdi-close' : 'mdi-magnify' }}</v-icon>
      </button>
      <button type="button" class="list-iconbtn new" aria-label="New conversation" @click="$emit('new-conversation')">
        <v-icon size="19">mdi-plus</v-icon>
      </button>
      <button type="button" class="list-iconbtn" aria-label="Refresh" @click="$emit('sync')">
        <v-progress-circular v-if="syncInProgress" indeterminate size="16" width="2" color="primary" />
        <v-icon v-else size="19">mdi-refresh</v-icon>
      </button>
    </div>

    <!-- Search + filter -->
    <div v-if="showSearch" class="conv-toolbar">
      <div class="conv-search">
        <v-icon size="18">mdi-magnify</v-icon>
        <input ref="searchInput" v-model="searchQuery" type="text" placeholder="Search messages…">
      </div>
      <div class="conv-seg">
        <button type="button" class="opt" :class="{ active: filterMode === 'all' }" @click="filterMode = 'all'">All</button>
        <button type="button" class="opt" :class="{ active: filterMode === 'unread' }" @click="filterMode = 'unread'">Unread</button>
      </div>
    </div>

    <!-- Conversations -->
    <div v-if="filteredConversations.length > 0" class="conv-scroll">
      <conversation-list-item
          v-for="conversation in filteredConversations"
          :key="conversation.id"
          :conversation="conversation"
          :is-active="conversation.id === activeConversationId"
          @click="handleSelectConversation"
          @delete="handleDeleteConversation"
      />
    </div>

    <!-- Empty state -->
    <div v-else class="conv-empty">
      <v-icon size="56" color="textSubtle">
        {{ searchQuery || filterMode === 'unread' ? 'mdi-message-off-outline' : 'mdi-message-outline' }}
      </v-icon>
      <div class="conv-empty-title">
        {{ searchQuery || filterMode === 'unread' ? 'No conversations found' : 'No conversations' }}
      </div>
      <div class="conv-empty-sub">
        {{ searchQuery || filterMode === 'unread'
          ? 'Try adjusting your search or filter'
          : 'Start a new conversation to begin messaging' }}
      </div>
      <v-btn
          v-if="!searchQuery && filterMode === 'all'"
          color="primary"
          rounded="pill"
          class="mt-4"
          prepend-icon="mdi-pencil"
          @click="$emit('new-conversation')"
      >
        New conversation
      </v-btn>
    </div>
  </div>
</template>

<style scoped lang="scss">
.conv-list {
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: rgb(var(--v-theme-surface));
}

/* header */
.list-head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px 10px;
  flex-shrink: 0;
}

.lh-title {
  flex: 1;
  font-size: 15.5px;
  font-weight: 700;
  letter-spacing: -0.2px;
}

.list-iconbtn {
  width: 36px;
  height: 36px;
  border-radius: 11px;
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgb(var(--v-theme-borderLight));
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgb(var(--v-theme-text));
  flex-shrink: 0;

  &:hover {
    background: rgb(var(--v-theme-light));
  }

  &.new {
    background: rgb(var(--v-theme-primary));
    border-color: rgb(var(--v-theme-primary));
    color: #fff;
  }
}

/* search + segmented filter */
.conv-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 16px 12px;
  flex-shrink: 0;
}

.conv-search {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  background: rgb(var(--v-theme-bg, 250, 250, 250));
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 999px;
  padding: 9px 14px;
  color: rgb(var(--v-theme-textSubtle));

  input {
    border: none;
    outline: none;
    background: none;
    font: inherit;
    font-size: 13px;
    width: 100%;
    color: rgb(var(--v-theme-text));
  }
}

.conv-seg {
  display: flex;
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 12px;
  padding: 3px;
  flex-shrink: 0;

  .opt {
    font-size: 12.5px;
    font-weight: 600;
    padding: 6px 12px;
    border-radius: 9px;
    color: rgb(var(--v-theme-charcoal));
    white-space: nowrap;

    &.active {
      background: rgb(var(--v-theme-text));
      color: #fff;
    }
  }
}

/* list scroll */
.conv-scroll {
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
}

/* empty state */
.conv-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 24px;
}

.conv-empty-title {
  font-size: 15px;
  font-weight: 700;
  margin-top: 12px;
}

.conv-empty-sub {
  font-size: 12.5px;
  color: rgb(var(--v-theme-textSubtle));
  margin-top: 4px;
}
</style>
